<template>
  <div>
    <ValidationObserver v-if="!needToAddStructure">
      <section>
        <ValidationProvider
          v-slot="{ errors, valid, handleBlur }"
          :name="$t('StructureSelectionView.chercher-structure')"
          :model-value="selectedStructure.nom || ''"
          @update:model-value="updateStructureSearch"
        >
          <b-field
            :type="{
              'is-danger': fieldTouched && (errors[0] || selectionError),
              'is-success': fieldTouched && valid && !selectionError,
            }"
            :message="
              selectionError ||
              errors + '' ||
              $t('StructureSelectionView.chercher-structure-precision')
            "
            class="StructureSelectionView-field"
          >
            <template #label>
              <div class="fieldLabel">
                <div class="innerLabel">{{ $t("StructureSelectionView.chercher-structure") }}</div>
                <div class="mandatory">{{ $t("obligatoire") }}</div>
              </div>
            </template>

            <b-autocomplete
              v-model="structureSearch"
              open-on-focus
              icon="search"
              clearable
              :data="filteredStructures"
              :placeholder="$t('StructureSelectionView.entrer-structure')"
              field="nom"
              @update:model-value="handleStructureSearchInput"
              @select="
                (option) => {
                  selectedStructure = option ?? new StructureDTO()
                  selectionError = ''
                  fieldTouched = true
                }
              "
              @blur="
                (event) => {
                  fieldTouched = true
                  handleBlur && handleBlur(event)
                }
              "
              @focus="filterStructures"
            >
              <template #default="props">
                <div>
                  <div>
                    {{ props.option.nom }}
                  </div>
                  <div>
                    {{ `${props.option.rue} ${props.option.codePostal} ${props.option.ville}` }}
                  </div>
                </div>
              </template>
              <template #footer>
                <section v-if="inputValid">
                  <a @click="maybeAskConfirmation">
                    <span>{{ $t("StructureSelectionView.ajouter-structure") }}</span>
                  </a>
                </section>
              </template>
              <template #empty>
                {{ $t("StructureSelectionView.no-result", [structureSearch]) }}
              </template>
            </b-autocomplete>
          </b-field>
          <b-modal
            v-model="isAskConfirmationActive"
            has-modal-card
            trap-focus
            :destroy-on-hide="true"
            close-button-aria-label="Close"
            aria-role="dialog"
            aria-label="ConfirmModale"
            aria-modal
            scroll="keep"
            @cancel="isAskConfirmationActive = false"
          >
            <div class="modal-card">
              <header class="modal-card-head">
                <H3TitleView>{{ $t("registerPage.confirmer-ajout-structure") }}</H3TitleView>
                <button
                  type="button"
                  class="delete"
                  aria-label="close"
                  @click="isAskConfirmationActive = false"
                ></button>
              </header>
              <section class="modal-card-body"></section>
              <footer class="modal-card-foot">
                <div class="buttons">
                  <div
                    class="hoverable-discrete-link mr-3"
                    @click="isAskConfirmationActive = false"
                  >
                    {{ $t("annuler") }}
                  </div>
                  <b-button rounded type="is-primary" @click="goAddStructure">
                    {{ $t("confirmer") }}
                  </b-button>
                </div>
              </footer>
            </div>
          </b-modal>
        </ValidationProvider>
      </section>

      <div class="buttons">
        <b-button v-if="enableBack" type="is-dark" @click="goBack">
          {{ $t("registerPage.precedent") }}
        </b-button>
        <b-button type="is-primary" @click="submit">
          <span v-if="isChanging">
            {{ $t("registerPage.valider-structure") }}
          </span>
          <span v-else>
            {{ $t("registerPage.inscription") }}
          </span>
        </b-button>
      </div>
    </ValidationObserver>

    <ValidationObserver v-if="needToAddStructure" v-slot="{ handleSubmit }">
      <SubTitleView>{{ $t("StructureSelectionView.creation-structure-title") }}</SubTitleView>
      <div class="structure-creation-info">
        {{ $t("StructureSelectionView.creation-structure-description") }}
      </div>
      <StructureUpdateView
        ref="structureUpdate"
        :structure="selectedStructure"
        @tab-index-changed="emitTabChanged"
      />

      <div class="buttons">
        <b-button type="is-dark" @click="goToPrevious">
          {{ $t("registerPage.precedent") }}
        </b-button>
        <b-button v-if="!lastTabDisplayed()" type="is-primary" @click="goToNextTab">
          {{ $t("registerPage.suivant") }}
        </b-button>
        <b-button v-else type="is-primary" @click="handleSubmit(createStructure)">
          {{ $t("StructureSelectionView.ajouter") }}
        </b-button>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { REGISTER_STEPS } from "@/model/bean/EnumHelper"
import { StructureDTO } from "@/model/bean/GeneratedDTOs"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import StructureUpdateView from "@/views/commons/StructureUpdateView.vue"
import SubTitleView from "@/views/commons/SubTitleView.vue"
import { BAutocomplete, BButton, BField, BModal } from "buefy"
import { defineComponent } from "vue"

export default defineComponent({
  name: "StructureSelectionView",
  components: {
    ValidationObserver,
    ValidationProvider,
    StructureUpdateView,
    SubTitleView,
    BButton,
    BField,
    BModal,
    BAutocomplete,
  },
  props: {
    modelValue: {
      type: Object as () => StructureDTO,
      default: () => undefined,
    },
    isChanging: {
      type: Boolean,
      default: false,
    },
    enableBack: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["go-back", "tab-index-changed", "update:modelValue"],
  data() {
    return {
      organismeService: OrganismeService.INSTANCE,
      selectedStructure: new StructureDTO() as StructureDTO,
      filteredStructures: [] as StructureDTO[],
      needToAddStructure: false,
      structureSearch: "",
      inputValid: false,
      isAskConfirmationActive: false,
      structureTabIndex: 0,
      structureNbTabs: 0,
      selectionError: "",
      fieldTouched: false,
    }
  },
  watch: {
    needToAddStructure(val: boolean): void {
      if (val) {
        this.$nextTick(() => {
          const ref = this.$refs.structureUpdate as InstanceType<typeof StructureUpdateView> | null
          if (ref) {
            this.structureNbTabs = ref.numberOfTabs
          }
        })
      }
    },
    structureSearch(newVal: string): void {
      if (this.selectedStructure.nom && newVal !== this.selectedStructure.nom) {
        this.selectedStructure = new StructureDTO()
        this.selectionError = ""
      }
    },
  },
  created(): void {
    if (this.modelValue) {
      this.selectedStructure = this.modelValue
      this.structureSearch = this.modelValue.nom || ""
      this.fieldTouched = true
    }
  },
  methods: {
    updateStructureSearch(newValue: string): void {
      this.structureSearch = newValue
      if (newValue !== this.selectedStructure.nom) {
        this.selectedStructure = new StructureDTO()
      }
      this.filterStructures()
    },

    handleStructureSearchInput(newValue: string | number): void {
      this.structureSearch = String(newValue)
      if (String(newValue) !== this.selectedStructure.nom) {
        this.selectedStructure = new StructureDTO()
      }
      this.filterStructures()
    },

    filterStructures(): void {
      const search = this.structureSearch.toLowerCase()
      this.inputValid = search.length >= 2
      if (search.length < 2) {
        this.filteredStructures = []
        return
      }
      this.organismeService.searchStructures(search).then((r) => {
        this.filteredStructures = []
        r.forEach((s) => this.filteredStructures.push(s))
      })
    },

    maybeAskConfirmation(): void {
      this.filteredStructures.length > 0
        ? (this.isAskConfirmationActive = true)
        : this.goAddStructure()
    },

    goAddStructure(): void {
      this.structureTabIndex = 0
      this.structureNbTabs = 0
      this.needToAddStructure = true
      this.updateURL(REGISTER_STEPS.STRUCTURE_ADDITION)
      this.isAskConfirmationActive = false
    },

    createStructure(): void {
      const ref = this.$refs.structureUpdate as InstanceType<typeof StructureUpdateView>
      if (ref) {
        ref.updateTetesStructure()
        this.selectedStructure = (ref.localStructure as StructureDTO) ?? new StructureDTO()
      }
      this.organismeService.addStructure(this.selectedStructure)
      this.structureSearch = this.selectedStructure.nom || ""
      this.filteredStructures.push(this.selectedStructure)
      this.needToAddStructure = false
      this.submit()
    },

    submit(): void {
      this.fieldTouched = true
      if (!this.selectedStructure.nom) {
        if (!this.structureSearch) {
          this.selectionError = this.$t("validation-input-invalid-required").toString()
        } else {
          this.selectionError = this.$t(
            "StructureSelectionView.chercher-structure-obligatoire",
          ).toString()
        }
        return
      }
      this.$emit("update:modelValue", this.selectedStructure)
    },

    goBack(): void {
      this.$emit("go-back", this.selectedStructure)
    },

    updateURL(nextStep: string): void {
      const params = this.$router.currentRoute.value.params
      if (nextStep !== params["registerStep"]) {
        params["registerStep"] = nextStep
        this.$router.push({ params: params })
      }
    },

    setTabIndex(tabIndex: number): void {
      //@ts-ignore
      this.$refs.structureUpdate && this.$refs.structureUpdate.setTabIndex(tabIndex)
    },

    emitTabChanged(tabIndex: number): void {
      this.structureTabIndex = tabIndex
      this.$emit("tab-index-changed", tabIndex)
    },

    goToNextTab(): void {
      //@ts-ignore
      this.$refs.structureUpdate && this.$refs.structureUpdate.goToNextTab()
    },

    lastTabDisplayed(): boolean {
      if (this.structureNbTabs === 0) return false
      return this.structureTabIndex === this.structureNbTabs - 1
    },

    goToPrevious(): void {
      const firstTabDisplayed =
        //@ts-ignore
        this.$refs.structureUpdate && this.$refs.structureUpdate.firstTabDisplayed()

      if (firstTabDisplayed) {
        this.needToAddStructure = false
      } else {
        //@ts-ignore
        this.$refs.structureUpdate && this.$refs.structureUpdate.goToPreviousTab()
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.StructureSelectionView-field {
  margin-bottom: $input-field-margin-bot;
}

.buttons {
  justify-content: space-between;
}
</style>
